/*
  Savings summary in the Essential cart drawer — Sableau
  ------------------------------------------------------------
  Shopify's own checkout already names the sale saving: each line shows its
  discount, the compare-at price is struck through, and a native "TOTAL SAVINGS"
  row sits under the total. What checkout never counts is the shipping that was
  waived — that row just reads "Free" — and on a Basic plan nothing can be added
  to the checkout steps, because Shopify restricts checkout UI extensions there
  to Plus. So the full picture has to land on the last screen we do control: the
  drawer, immediately above the checkout button.

  The drawer belongs to an app and takes no Liquid, so this runs from the theme
  and hooks the drawer's stable data-essential-cart-element attributes, the same
  way assets/cart-savings.js already appends its per-line "(Save €X)" labels.

  Every figure is read back off the screen rather than recomputed:
    - the sale saving is the sum of each line's struck-through price minus its
      charged price, i.e. exactly the numbers the shopper can already see;
    - the free-shipping threshold comes from the per-currency map in theme
      settings, so it is the same number the announcement bar, the product page
      and the cart page show;
    - the waived shipping fee is converted with the same rate and the same
      round-up rule local-money.js uses.

  If anything is missing — unknown currency, no subtotal, changed app markup —
  the script renders nothing rather than guessing. A silent no-op is the right
  failure mode; a wrong number one click before payment is exactly what this
  change set exists to remove.
*/
(function () {
  'use strict';

  if (window.__sbDrawerShippingLoaded) return;
  window.__sbDrawerShippingLoaded = true;

  var CONFIG = {};
  try {
    var node = document.getElementById('sb-free-shipping-config');
    if (node) CONFIG = JSON.parse(node.textContent || '{}');
  } catch (e) { /* leave CONFIG empty; the guards below stop the script */ }

  var SUBTOTAL = '[data-essential-cart-element="subtotal-wrapper"]';
  var FOOTER = '[data-essential-cart-element="cart-drawer-footer"]';
  var CHECKOUT = '[data-essential-cart-element="checkout-button-wrapper"]';
  var ITEM = '[data-essential-cart-element="cart-drawer-item"]';
  var REG = '[data-essential-cart-element="line-item-regular-price"]';
  var DISC = '[data-essential-cart-element="line-item-discounted-price"]';
  var MARK = 'sb-savings';

  var STYLE =
    '.sb-savings{margin:0 0 .7rem;padding:.6rem .7rem;border-radius:6px;' +
    'background:#f2f9f4;border:1px solid #d7e9dc;font-size:.8rem;line-height:1.45}' +
    '.sb-savings--near{background:#f6f5f3;border-color:#e3e0da}' +
    '.sb-savings__row{display:flex;justify-content:space-between;gap:1rem;color:#3d4a41}' +
    '.sb-savings__row--total{margin-top:.3rem;padding-top:.3rem;' +
    'border-top:1px solid #cfe2d5;color:#0a7a42;font-weight:700}' +
    '.sb-savings__nudge{display:flex;gap:.4rem;color:#5c574f;font-weight:500}';

  // Same list local-money.js uses: currencies whose everyday amounts carry no decimals.
  var WHOLE_UNIT = ['NOK', 'SEK', 'DKK', 'ISK', 'JPY', 'HUF', 'CZK', 'KRW', 'CLP'];
  var PRICE_ONLY = /^\s*(?:[^\d\s]{0,4}\s?)?\d[\d.,\s ]*(?:\s?[^\d\s]{0,4})?\s*$/;

  function ctx() {
    var shop = window.Shopify || {};
    var lm = window.__localMoney || {};
    return {
      currency: (shop.currency && shop.currency.active) || 'EUR',
      rate: parseFloat((shop.currency && shop.currency.rate) || '1') || 1,
      euZone: lm.euZone !== false
    };
  }

  function thresholdUnits(currency, rate) {
    var pairs = String(CONFIG.thresholds || '').replace(/\s/g, '').split(',');
    for (var i = 0; i < pairs.length; i++) {
      var bits = pairs[i].split(':');
      if (bits.length === 2 && bits[0].toUpperCase() === currency) {
        var n = parseFloat(bits[1]);
        if (!isNaN(n) && n > 0) return n;
      }
    }
    /* Not in the map. Derive it with the same rule local-money.js uses to print the
       cut-off on the product page: convert with the live market rate, then round up
       by one unit of that currency.

       That rule used to jump to the next 50 above 100, on the belief that Shopify
       rounds the cut-off to something clean. Measured against the live store on
       12-09-2026 (store/sableau/tools/meet-verzenddrempel.sh): it does not. The
       shipping profile converts with the raw rate and rounds nothing, so the real
       cut-off is 79 x rate — 906.74 kr in Sweden, where we were printing 950, and
       868.32 kr in Norway, where the map said 900. Both told a shopper who already
       had free shipping that he still had to add more, on the screen right before
       the checkout button.

       Still rounding UP, by one unit: asking a crown too much and then getting it
       free anyway is a good surprise, the reverse is a broken promise. */
    var base = parseFloat(CONFIG.thresholdBase);
    if (isNaN(base) || base <= 0 || !rate) return 0;
    var value = base * rate;
    /* 29-09-2026: whole units in every currency. "£69.15" read as a sum, not a promise; "£70" is at most
       one unit above the real cut-off, the same safe side as above. local-money.js rounds the same way. */
    return Math.ceil(value);
  }

  /* The subtotal wrapper repeats its label and amount
     ("Subtotal: €99,99Subtotal€99,99"), so reading it wholesale drags the label
     into the number. Walk down to the leaf that holds nothing but a price. */
  function priceLeaf(root) {
    if (!root) return null;
    var nodes = root.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].children.length) continue;
      var t = (nodes[i].textContent || '').replace(/ /g, ' ').trim();
      if (t && t.length <= 24 && /\d/.test(t) && PRICE_ONLY.test(t)) return t;
    }
    var own = (root.textContent || '').replace(/ /g, ' ').trim();
    return PRICE_ONLY.test(own) ? own : null;
  }

  /* "€99,99" · "kr 1.113,07" · "1 158,00 kr" · "£85.00" -> cents plus the
     separators and affixes in use, so anything rendered back matches the
     market's own formatting. */
  function parseAmount(str) {
    var txt = String(str).replace(/ /g, ' ');
    var m = txt.match(/(\d[\d.,\s]*\d|\d)/);
    if (!m) return null;
    var numStr = m[1];
    var dec = numStr.match(/([.,])(\d{2})\s*$/);
    var intPart = (dec ? numStr.slice(0, dec.index) : numStr).replace(/[.,\s]/g, '');
    if (!intPart) return null;
    var thou = '';
    var t = (numStr.match(/\d([.,\s])\d{3}/) || [])[1];
    if (t) thou = t;
    return {
      cents: parseInt(intPart, 10) * 100 + (dec ? parseInt(dec[2], 10) : 0),
      decSep: dec ? dec[1] : ',',
      thouSep: thou,
      prefix: txt.slice(0, txt.indexOf(numStr)).trim(),
      suffix: txt.slice(txt.indexOf(numStr) + numStr.length).trim()
    };
  }

  function format(sample, cents, currency) {
    var whole = Math.floor(cents / 100);
    var frac = String(cents % 100);
    if (frac.length < 2) frac = '0' + frac;
    var ws = String(whole);
    if (sample.thouSep) ws = ws.replace(/\B(?=(\d{3})+(?!\d))/g, sample.thouSep);
    var body = WHOLE_UNIT.indexOf(currency) !== -1 && cents % 100 === 0
      ? ws
      : ws + sample.decSep + frac;
    // A symbol sits against the number ("£19.05", "€19,05"); only a word prefix ("kr", "CHF") gets a space.
    var gap = /[A-Za-z]$/.test(sample.prefix || '') ? ' ' : '';
    return (sample.prefix ? sample.prefix + gap : '') + body + (sample.suffix ? ' ' + sample.suffix : '');
  }

  function shippingSavedCents(c) {
    // One figure worldwide since 2026-08-20; feeInternational is no longer read.
    var eur = parseFloat(CONFIG.feeDomestic);
    if (isNaN(eur) || eur <= 0) return 0;
    var value = eur * c.rate;
    // local-money.js rounds a fee up, so the figure is never understated.
    if (WHOLE_UNIT.indexOf(c.currency) !== -1) return Math.ceil(value) * 100;
    return Math.ceil(value * 20) * 5; // the next 0.05, exactly as local-money.js shows the fee ("£6.10", not "£6.08")
  }

  /* Sale saving = what the shopper can already see per line: the struck-through
     price minus the price actually charged, summed. Reading it back off the
     drawer keeps it identical to the "(Save €X)" labels next to it. */
  function saleSavedCents() {
    var items = document.querySelectorAll(ITEM);
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var reg = parseAmount(priceLeaf(items[i].querySelector(REG)) || '');
      var disc = parseAmount(priceLeaf(items[i].querySelector(DISC)) || '');
      if (reg && disc && reg.cents > disc.cents) total += reg.cents - disc.cents;
    }
    return total;
  }

  function row(label, value, cls) {
    var el = document.createElement('div');
    el.className = 'sb-savings__row' + (cls ? ' ' + cls : '');
    var a = document.createElement('span');
    a.textContent = label;
    var b = document.createElement('span');
    b.textContent = value;
    el.appendChild(a);
    el.appendChild(b);
    return el;
  }

  function apply() {
    var footer = document.querySelector(FOOTER) || document.querySelector(CHECKOUT);
    if (!footer) return;

    var c = ctx();
    var units = thresholdUnits(c.currency, c.rate);
    if (!units) return;

    var subTxt = priceLeaf(document.querySelector(SUBTOTAL));
    var sample = subTxt && parseAmount(subTxt);
    if (!sample || !sample.cents) return;

    var thresholdCents = Math.round(units * 100);
    var free = sample.cents >= thresholdCents;
    var sale = saleSavedCents();
    var ship = free ? shippingSavedCents(c) : 0;
    var total = sale + ship;

    /* Signature keeps the observer from rebuilding identical markup on every
       drawer mutation, which would otherwise loop. */
    var signature = [c.currency, sample.cents, free, sale, ship].join('|');
    var el = footer.querySelector('.' + MARK);
    if (el && el.getAttribute('data-sb-signature') === signature) return;

    if (!el) {
      el = document.createElement('div');
      var anchor = footer.querySelector(CHECKOUT);
      if (anchor) footer.insertBefore(el, anchor);
      else footer.insertBefore(el, footer.firstChild);
    }
    el.textContent = '';
    el.setAttribute('data-sb-signature', signature);

    if (!free) {
      // Nothing earned on shipping yet, so show the distance to it instead.
      el.className = MARK + ' ' + MARK + '--near';
      var nudge = document.createElement('div');
      nudge.className = 'sb-savings__nudge';
      var arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      var copy = document.createElement('span');
      copy.appendChild(document.createTextNode('Add '));
      var amt = document.createElement('b');
      amt.textContent = format(sample, thresholdCents - sample.cents, c.currency);
      copy.appendChild(amt);
      copy.appendChild(document.createTextNode(' for free shipping'));
      /* Same reasoning as the cart page: the shopper below the threshold is the
         one who needs to know what the trip is worth. Skipped when the fee does
         not resolve, so an unknown zone never prints a bare "save". */
      var wouldSave = shippingSavedCents(c);
      if (wouldSave > 0) {
        copy.appendChild(document.createTextNode(' \u2014 save '));
        var save = document.createElement('b');
        save.textContent = format(sample, wouldSave, c.currency);
        copy.appendChild(save);
      }
      nudge.appendChild(arrow);
      nudge.appendChild(copy);
      el.appendChild(nudge);
      if (sale > 0) el.appendChild(row('Sale discount', format(sample, sale, c.currency)));
      return;
    }

    el.className = MARK;
    if (sale > 0) el.appendChild(row('Sale discount', format(sample, sale, c.currency)));
    if (ship > 0) el.appendChild(row('Shipping', format(sample, ship, c.currency)));
    if (total > 0) {
      el.appendChild(row('You save', format(sample, total, c.currency), 'sb-savings__row--total'));
    } else {
      el.appendChild(row('Shipping', 'Free', 'sb-savings__row--total'));
    }
  }

  function start() {
    var style = document.createElement('style');
    style.textContent = STYLE;
    document.head.appendChild(style);

    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; try { apply(); } catch (e) {} });
    }
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    var n = 0;
    var timer = setInterval(function () { schedule(); if (++n > 20) clearInterval(timer); }, 1000);
    schedule();
  }

  /* Test seam for store/sableau/tools/test-verzenddrempel.mjs. Nothing in the storefront reads it; it exists
     so the one number a shopper acts on — "add X more for free shipping" — can be checked against the four
     markets that were measured against the live store, outside a browser. */
  window.__sbDrawerShipping = { thresholdUnits: thresholdUnits };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
